import { FormEvent, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import type { Location } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { PasswordInput } from "../components/PasswordInput";
import { useAuth } from "../auth/AuthContext";
import type { ApiError } from "../api/client";
import { authFallbackError, looksLikeEmail } from "../lib/authErrors";
import { FRONTEND_VERSION, formatVersion, useBackendVersion } from "../lib/appVersion";

type LocationState = { from?: Location };
type TFn = (key: string, fallback: string) => string;

export default function LoginPage() {
  const { login } = useAuth();
  const { t } = useTranslation("auth");
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as LocationState | undefined)?.from?.pathname ?? "/";

  // Set by AuthContext when a dead session forced the redirect here — read
  // once so the notice survives exactly one render of the login page.
  const [sessionExpired] = useState(() => {
    try {
      const flag = sessionStorage.getItem("bkm.sessionExpired") === "1";
      sessionStorage.removeItem("bkm.sessionExpired");
      return flag;
    } catch {
      return false;
    }
  });

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    // Phone keyboards append a space after an autocompleted address.
    const address = email.trim();
    if (!looksLikeEmail(address)) {
      setError(t("errors.emailInvalid", "Unesite celu adresu e-pošte, npr. ime@firma.rs."));
      return;
    }
    if (!password) {
      setError(t("errors.passwordRequired", "Unesite lozinku."));
      return;
    }
    setSubmitting(true);
    try {
      await login(address, password, rememberMe);
      // `afterLogin` lets RequirePermission send a user who cannot see `from`
      // (a different account signed in after the session died on an admin page)
      // to the dashboard instead of a dead-end 403.
      navigate(from, { replace: true, state: { afterLogin: true } });
    } catch (err) {
      const apiErr = err as ApiError;
      setError(translateError(apiErr, t));
    } finally {
      setSubmitting(false);
    }
  }

  // Which build this is, for a support call before anyone is signed in (2026-10-09).
  const backendVersion = useBackendVersion();

  return (
    <div style={shellStyle}>
      <form onSubmit={handleSubmit} style={cardStyle} noValidate>
        <img src="/assets/bankom-logo.svg" alt="Bankom" className="auth-logo" style={logoStyle} />
        <h1 style={titleStyle}>{t("login.title", "Prijava")}</h1>
        <p style={subtitleStyle}>{t("login.subtitle", "Bankom CMMS · Održavanje")}</p>

        {sessionExpired && !error && (
          <div style={{ ...errorStyle, background: "rgba(31,111,178,.12)", borderColor: "var(--border)", color: "var(--info, var(--info))" }}>
            {t("login.sessionExpired", "Sesija je istekla. Prijavite se ponovo.")}
          </div>
        )}
        {error && <div style={errorStyle} role="alert">{error}</div>}

        <label style={labelStyle}>
          <span>{t("login.email", "E-pošta")}</span>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
            required
            style={inputStyle}
          />
        </label>

        <label style={labelStyle}>
          <span>{t("login.password", "Lozinka")}</span>
          <PasswordInput
            value={password}
            onChange={setPassword}
            autoComplete="current-password"
            required
            inputStyle={inputStyle}
          />
        </label>

        <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 14 }}>
          <input
            type="checkbox"
            checked={rememberMe}
            onChange={(e) => setRememberMe(e.target.checked)}
          />
          {t("login.rememberMe", "Zapamti me na ovom uređaju")}
        </label>

        <button type="submit" disabled={submitting} style={primaryButton}>
          {submitting ? t("login.submitting", "Prijavljivanje…") : t("login.submit", "Prijavi se")}
        </button>

        <Link to="/forgot-password" style={linkStyle}>
          {t("login.forgot", "Zaboravili ste lozinku?")}
        </Link>
        <div className="auth-version" title={t("app.version", "Verzija aplikacije")}>Bankom CMMS {formatVersion(backendVersion ?? FRONTEND_VERSION)}</div>
      </form>
    </div>
  );
}

function translateError(err: ApiError, t: TFn): string {
  switch (err.code) {
    case "INVALID_CREDENTIALS":
      return t("errors.invalidCredentials", "Pogrešna e-pošta ili lozinka.");
    case "ACCOUNT_LOCKED":
      return t("errors.accountLocked", "Vaš nalog je privremeno zaključan. Pokušajte kasnije ili resetujte lozinku.");
    case "ACCOUNT_DISABLED":
      return t("errors.accountDisabled", "Ovaj nalog je onemogućen. Obratite se administratoru.");
    case "VALIDATION":
      // The address passed the shape check but not the server's @Email.
      return t("errors.emailInvalid", "Unesite celu adresu e-pošte, npr. ime@firma.rs.");
    default:
      return authFallbackError(err, t);
  }
}

const logoStyle: React.CSSProperties = {
  // The Bankom Group mark is 467x113 (4.1:1); 160px keeps the same visual weight
  // the previous 2.6:1 raster logo had at 96px.
  width: 160,
  height: "auto",
  alignSelf: "center",
  marginBottom: 8,
};

const shellStyle: React.CSSProperties = {
  minHeight: "100vh",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  background: "var(--bg)",
  padding: 16,
};

const cardStyle: React.CSSProperties = {
  width: "100%",
  maxWidth: 400,
  background: "var(--surface)",
  padding: 32,
  borderRadius: 12,
  boxShadow: "0 1px 3px rgba(0,0,0,.08)",
  display: "flex",
  flexDirection: "column",
  gap: 16,
};

const titleStyle: React.CSSProperties = { margin: 0, color: "var(--brand-primary)", fontSize: 24 };
const subtitleStyle: React.CSSProperties = { margin: 0, color: "var(--ink-3)", fontSize: 14 };
const labelStyle: React.CSSProperties = { display: "flex", flexDirection: "column", gap: 6, fontSize: 14 };
const inputStyle: React.CSSProperties = {
  padding: "10px 12px",
  borderRadius: 6,
  border: "1px solid var(--border-strong)",
  // Explicit so the dark theme does not keep the browser's white field with light text.
  background: "var(--surface)",
  color: "var(--ink)",
  fontSize: 15,
};
const primaryButton: React.CSSProperties = {
  padding: "12px 16px",
  background: "var(--brand-primary)",
  color: "#fff",
  border: "none",
  borderRadius: 6,
  fontSize: 15,
  fontWeight: 600,
  cursor: "pointer",
};
const linkStyle: React.CSSProperties = {
  color: "var(--brand-primary)",
  fontSize: 14,
  textDecoration: "none",
  textAlign: "center",
};
const errorStyle: React.CSSProperties = {
  padding: 12,
  background: "rgba(182,53,58,.12)",
  color: "var(--err)",
  borderRadius: 6,
  fontSize: 14,
};
